import "./career.css";
import SEO from "../../SEO";

/* ── Data ──────────────────────────────────────────────────────────────────── */
const heroPerks = [
  "Cutting-edge AI Projects",
  "Flexible Work Environment",
  "Competitive Compensation",
  "Fast Growth Path",
];

const whyJoin = [
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    ),
    title: "Cutting-Edge AI Projects",
    desc: "Work on real generative AI systems — RAG pipelines, LLM agents, and intelligent applications — not toy demos.",
  },
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4l3 3" />
      </svg>
    ),
    title: "Learn & Grow Continuously",
    desc: "Structured mentorship, dedicated learning time, and exposure to the latest tools — your career accelerates here.",
  },
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: "Innovative Team Culture",
    desc: "A flat, collaborative environment where your ideas matter and the best solution wins — regardless of seniority.",
  },
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    title: "Competitive Compensation",
    desc: "Market-aligned salaries, performance bonuses, and benefits that grow with you as you deliver impact.",
  },
];

const roles = [
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    ),
    title: "AI Engineer",
    stack: ["Python", "LangChain", "LLMs"],
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    title: "Full Stack Developer",
    stack: ["React", "Node.js", "TypeScript"],
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <path d="M12 18h.01" />
      </svg>
    ),
    title: "Mobile App Developer",
    stack: ["React Native", "Expo"],
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    title: "DevOps Engineer",
    stack: ["AWS", "Docker", "Kubernetes"],
  },
];

const lookFor = [
  {
    num: "01",
    title: "Strong Problem-Solving Skills",
    desc: "Break down ambiguous challenges into clear, testable solutions — first principles thinking matters here.",
  },
  {
    num: "02",
    title: "Passion for New Technologies",
    desc: "You follow the frontier — AI, cloud, developer tooling — and you learn by building, not just reading.",
  },
  {
    num: "03",
    title: "Team Collaboration Mindset",
    desc: "You communicate early, ask questions, review code generously, and make everyone around you better.",
  },
  {
    num: "04",
    title: "Real-World Project Experience",
    desc: "You've shipped something users actually use. Production scars make better engineers.",
  },
];

const growth = [
  {
    num: "01",
    title: "Scalable Systems Exposure",
    desc: "Work on production architectures serving real users — not just internal tools or sandbox environments.",
  },
  {
    num: "02",
    title: "AI & Modern Tech Stack",
    desc: "Deep access to GenAI, LangChain, vector databases, and the latest cloud-native engineering patterns.",
  },
  {
    num: "03",
    title: "Career Advancement",
    desc: "Clear growth tracks from IC to tech lead — we promote from within and invest in leadership development.",
  },
  {
    num: "04",
    title: "Leadership Opportunities",
    desc: "Own features end-to-end, mentor junior team members, and shape the technical direction of real products.",
  },
];

/* ── Arrow icon ─────────────────────────────────────────────────────────────── */
const ArrowRight = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────────────────── */
export default function Career() {
  return (
    <>
      <SEO
        title="Careers: AI, Web & Software Engineering | Best Code Technology"
        description="Explore career opportunities in AI, web and mobile development."
        keywords="AI jobs, software jobs, developer careers"
        url="https://bestcodetechnology.in/about/career"
      />

      <div className="ca-page">
        {/* ══════════════════════════════════════════
          HERO
      ══════════════════════════════════════════ */}
        <section className="ca-hero">
          <div className="ca-hero__orb2" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <div className="ca-hero__eyebrow">
                  <span className="ca-tag ca-tag--dark">
                    Careers at Best Code Technology
                  </span>
                </div>

                <h1 className="ca-hero__headline">
                  Join Our Team &amp; <br />
                  <span>Build the Future</span>
                </h1>

                <p className="ca-hero__desc">
                  We're always looking for talented individuals who are
                  passionate about technology, AI, and innovation. If you love
                  solving hard problems and building things that last — you'll
                  fit right in.
                </p>

                <div className="ca-hero__perks">
                  {heroPerks.map((p) => (
                    <span key={p} className="ca-hero__perk">
                      <span className="ca-hero__perk-dot" aria-hidden="true" />
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
          WHY JOIN US
      ══════════════════════════════════════════ */}
        <section className="ca-why">
          <div className="container-xl">
            <div className="row justify-content-center ca-why__header">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <p className="ca-why__pretitle">Our Culture</p>
                <h2 className="ca-why__heading">Why Join Us</h2>
                <p className="ca-why__sub">
                  Four reasons why our engineers stay, grow, and do their best
                  work here.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {whyJoin.map((item, i) => (
                <div key={i} className="col-12 col-md-6 col-xl-3">
                  <div className="ca-why-card">
                    <div className="ca-why-card__icon">{item.icon}</div>
                    <h3 className="ca-why-card__title">{item.title}</h3>
                    <p className="ca-why-card__desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
          OPEN ROLES
      ══════════════════════════════════════════ */}
        <section className="ca-roles">
          <div className="container-xl">
            <div className="row justify-content-center ca-roles__header">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="ca-tag ca-tag--light mb-4 d-inline-flex">
                  Areas We Hire For
                </span>
                <h2 className="svc-section-title">
                  Always Open to Strong Engineers
                </h2>
                <p className="svc-section-sub">
                  We don't have specific vacancies posted right now, but we're
                  always glad to hear from people working across these areas.
                  Send us your portfolio and a short note about what you'd want
                  to build — we'll reach out when something opens up.
                </p>
              </div>
            </div>

            <div className="row g-3">
              {roles.map((role, i) => (
                <div key={i} className="col-12 col-lg-6">
                  <div className="ca-role-card">
                    <div className="ca-role-card__icon">{role.icon}</div>
                    <div className="ca-role-card__body">
                      <h3 className="ca-role-card__title">{role.title}</h3>
                      <div className="ca-role-card__tags">
                        {role.stack.map((s) => (
                          <span
                            key={s}
                            className="ca-role-card__tag ca-role-card__tag--stack"
                          >
                            {s}
                          </span>
                        ))}
                        <span className="ca-role-card__tag ca-role-card__tag--type">
                          {role.type}
                        </span>
                      </div>
                    </div>
                    <span className="ca-role-card__arrow">
                      <ArrowRight />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
          WHAT WE LOOK FOR  +  GROWTH OPPORTUNITIES
      ══════════════════════════════════════════ */}
        <section className="ca-qualities">
          <div className="container-xl">
            <div className="row justify-content-center ca-qualities__header">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <p className="ca-why__pretitle">The Details</p>
                <h2 className="ca-why__heading">What to Expect</h2>
                <p className="ca-why__sub">
                  What we look for in candidates — and what we commit to giving
                  in return.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {/* What We Look For */}
              <div className="col-12 col-lg-6">
                <div className="ca-split-panel ca-split-panel--look">
                  <div>
                    <span className="ca-split-panel__label">
                      What We Look For
                    </span>
                    <h3 className="ca-split-panel__title">
                      The Qualities We Value
                    </h3>
                  </div>
                  <div className="ca-split-panel__divider" aria-hidden="true" />
                  <ul className="ca-quality-list">
                    {lookFor.map((q) => (
                      <li key={q.num} className="ca-quality-item">
                        <div className="ca-quality-item__num">{q.num}</div>
                        <div className="ca-quality-item__body">
                          <p className="ca-quality-item__title">{q.title}</p>
                          <p className="ca-quality-item__desc">{q.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Growth Opportunities */}
              <div className="col-12 col-lg-6">
                <div className="ca-split-panel ca-split-panel--grow">
                  <div>
                    <span className="ca-split-panel__label">
                      Growth Opportunities
                    </span>
                    <h3 className="ca-split-panel__title">
                      What You'll Gain Here
                    </h3>
                  </div>
                  <div className="ca-split-panel__divider" aria-hidden="true" />
                  <ul className="ca-quality-list">
                    {growth.map((g) => (
                      <li key={g.num} className="ca-quality-item">
                        <div className="ca-quality-item__num">{g.num}</div>
                        <div className="ca-quality-item__body">
                          <p className="ca-quality-item__title">{g.title}</p>
                          <p className="ca-quality-item__desc">{g.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
          CTA BANNER
      ══════════════════════════════════════════ */}
        <section className="ca-cta">
          <div className="container-xl">
            <div className="ca-cta__inner">
              <div className="row justify-content-center">
                <div className="col-12 col-md-10 col-lg-8 text-center ca-cta__content">
                  <p className="ca-cta__eyebrow">Ready to Apply?</p>
                  <h2 className="ca-cta__heading">
                    Introduce Yourself Anyway.
                  </h2>
                  <p className="ca-cta__sub">
                    We're always open to exceptional talent. Send us your
                    portfolio, GitHub, or anything that shows us how you think
                    and build.
                  </p>
                  <div className="ca-cta__email-row justify-content-center">
                    <div className="ca-cta__email-badge">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      hr@bestcodetechnology.in
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
